/**
 * Complexity controller lifecycle machine.
 *
 * State graph:
 *
 *   idle ──SESSION_STARTED──▶ active
 *
 *   active ──REST_INJECTED──▶ restPending          (countRest)
 *   restPending ──REST_COMPLETED──▶ active
 *
 *   * ──RESET──▶ idle                              (clearCounts)
 *
 * `REST_INJECTED` is only accepted from `active`, which is what prevents two
 * rest phrases stacking. `REST_COMPLETED` is only accepted from `restPending`,
 * so a stray completion for an ordinary phrase cannot desynchronise the phase.
 *
 * Pure: the only observable output is `context.restsInjected` plus the phase.
 */

import { setup, assign } from 'xstate';

import type { ComplexityContext, ComplexityEvent } from './complexityMachine.types';

const initialContext: ComplexityContext = {
  restsInjected: 0,
};

export const complexityMachine = setup({
  types: {
    context: {} as ComplexityContext,
    events: {} as ComplexityEvent,
  },
  actions: {
    countRest: assign({ restsInjected: ({ context }) => context.restsInjected + 1 }),
    clearCounts: assign({ restsInjected: () => 0 }),
  },
}).createMachine({
  id: 'complexity',
  initial: 'idle',
  context: initialContext,
  on: {
    // A hard reset can arrive in any state: tier change (the scene instance is
    // reused across tiers), session end, or teardown.
    RESET: { target: '.idle', actions: 'clearCounts' },
  },
  states: {
    idle: {
      on: {
        SESSION_STARTED: 'active',
      },
    },

    active: {
      on: {
        REST_INJECTED: { target: 'restPending', actions: 'countRest' },
      },
    },

    restPending: {
      on: {
        REST_COMPLETED: 'active',
      },
    },
  },
});

export type ComplexityMachine = typeof complexityMachine;
